import {
  PaymentElement,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import { tt } from "../../utils/i18n/i18n";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  setError,
  setLoading,
  useLoadingSelector
} from "../../redux/actions/app";
import TestsID from "../../utils/TestsID";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../designsystem/KC_Button/KC_Button";
import "./styles.scss";
import { type StripeElements } from "@stripe/stripe-js";
import { type SaasCommitment } from "../../model/SaasTypes";
import { DEFAULT_PLAN_OPTION } from "../../utils/constants";
import CGVCheckList from "../CGVChecklist/CGVCheckList";
import { FullScreenLoader } from "../Loaders/FullScreenLoader/FullScreenLoader";
import BillDTO from "@tootsweet/model/datatransfer/BillDTO";
import KC_UIText from "../../designsystem/typography/KC_UIText/KC_UIText";

interface Props {
  amount: number;
  handlePrev?: () => void;
  isSaasSubscriptionForm: boolean;
  saasPlanId?: string | null;
  saasCommitment?: SaasCommitment | null;
  isForPromoCode2?: boolean;
  bill?: BillDTO | null;
  formattedButtonText: string;
  buttonSubText?: string;
  isFirstMonthFree?: boolean;
  isSetupIntent?: boolean;
}

export default function CheckoutFormInner(props: Props) {
  const stripe = useStripe();
  const elements: StripeElements | null = useElements();
  const dispatch = useDispatch();
  const isLoading = useLoadingSelector();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (stripe == null || elements == null) {
      return;
    }

    if (
      props.isSaasSubscriptionForm &&
      !props.isForPromoCode2 &&
      (!props.saasCommitment || !props.saasPlanId)
    ) {
      alert("Error: no plan or no duration specified");
      return;
    }

    dispatch(setLoading(true));

    let return_url = window.location.href;

    if (props.bill) {
      const url = new URL(return_url);
      url.searchParams.set("billId", props.bill.id);
      return_url = url.toString();
    }

    if (props.isSetupIntent) {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url
        }
      });

      if (result?.error?.message) {
        dispatch(setError(result.error.message));
      } else {
        alert(tt("api_error"));
      }

      dispatch(setLoading(false));
      return;
    }

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url
      }
    });

    if (result?.error?.message) {
      dispatch(setError(result.error.message));
    } else {
      alert(tt("api_error"));
    }

    dispatch(setLoading(false));
  };

  const BUTTON = useMemo(() => {
    if (props.isSaasSubscriptionForm) {
      return (
        <>
          <div className="w-100 d-none d-md-block">
            <KC_Button
              id={TestsID.checkoutButton}
              size={KC_ButtonSize.Large}
              text={props.formattedButtonText}
              disabled={isLoading}
              type={KC_ButtonType.Submit}
              isSecondary={true}
              className="w-50 ml-auto"
            />
          </div>
          <div className="d-block d-md-none">
            <KC_Button
              id={TestsID.checkoutButton}
              size={KC_ButtonSize.Large}
              text={props.formattedButtonText}
              disabled={isLoading}
              type={KC_ButtonType.Submit}
              isSecondary={true}
            />
          </div>
        </>
      );
    }
    return (
      <div className="checkout-inner-form__buttons-container d-flex justify-content-center justify-content-sm-end">
        {props.handlePrev != null ? (
          <div>
            <KC_Button
              id={TestsID.SAAS_FLYERS_ORDER_PREV_BUTTON}
              size={KC_ButtonSize.Large}
              text={tt("saas_flyers_order_payment_action_prev")}
              onClick={props.handlePrev}
              isInverted={true}
              type={KC_ButtonType.Button}
            />
          </div>
        ) : (
          <div id="dummy" className="d-none d-sm-block"></div>
        )}
        <div>
          <KC_Button
            id={TestsID.checkoutButton}
            size={KC_ButtonSize.Large}
            text={props.formattedButtonText}
            disabled={isLoading}
            type={KC_ButtonType.Submit}
            isSecondary={true}
          />
        </div>
      </div>
    );
  }, [
    props.handlePrev,
    isLoading,
    props.formattedButtonText,
    props.isSaasSubscriptionForm
  ]);

  return (
    <form onSubmit={handleSubmit}>
      <div>
        <PaymentElement />
        <FullScreenLoader loading={isLoading} />
      </div>
      <div>
        {props.saasCommitment && (
          <CGVCheckList
            amountToPay={props.amount}
            showMonthlyPayment={props.saasCommitment !== DEFAULT_PLAN_OPTION}
          />
        )}
        {BUTTON}
        {!!props.buttonSubText && (
          <div className="d-flex flex-row align-items-center justify-content-end mt-4 w-100 st-gap">
            <KC_UIText
              text={props.buttonSubText}
              className="text-right"
              weight="reg"
              fontStyle="italic"
            />
          </div>
        )}
      </div>
    </form>
  );
}
